import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      className={`w-full bg-white dark:bg-gray-900 sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg backdrop-blur-sm bg-white/95 dark:bg-gray-900/95' : 'shadow-sm'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="max-w-7xl mx-auto flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900 dark:text-gray-100 font-inter">DeviceLink</span>
                <span className="text-xl font-bold text-sky-500 font-inter"> Global</span>
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <motion.a 
              href="#home" 
              className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200"
              whileHover={{ y: -1 }}
            >
              Home
            </motion.a>
            <motion.a 
              href="#features" 
              className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200"
              whileHover={{ y: -1 }}
            >
              Features
            </motion.a>
            <motion.a 
              href="#pricing" 
              className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200"
              whileHover={{ y: -1 }}
            >
              Pricing
            </motion.a>
            <motion.a 
              href="#contact" 
              className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200"
              whileHover={{ y: -1 }}
            >
              Contact
            </motion.a>
            
            {/* Theme Toggle */}
            <ThemeToggle variant="header" size="medium" />
          </nav>

          {/* Client Area Button */}
          <div className="hidden md:block">
            <Link to="/auth/login">
              <motion.button 
                className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                Client Portal
              </motion.button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col space-y-4">
              <a href="#home" className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200">
                Home
              </a>
              <a href="#features" className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200">
                Features
              </a>
              <a href="#pricing" className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200">
                Pricing
              </a>
              <a href="#contact" className="text-gray-700 dark:text-gray-300 hover:text-sky-500 dark:hover:text-sky-400 font-medium transition-colors duration-200">
                Contact
              </a>
              <Link to="/auth/login">
                <button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-2.5 rounded-full font-semibold w-fit shadow-lg">
                  Client Portal
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
